// LaTeX commands read as a symbol in plain text; any other command is
// dropped (`\left`, `\displaystyle`) unless it names a function (`\sin`).
const MATH_SYMBOLS: Record<string, string> = {
  times: '×',
  cdot: '·',
  div: '÷',
  pm: '±',
  le: '≤',
  leq: '≤',
  ge: '≥',
  geq: '≥',
  neq: '≠',
  approx: '≈',
  to: '→',
  rightarrow: '→',
  infty: '∞',
  degree: '°',
  circ: '°',
  pi: 'π',
  theta: 'θ',
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  Delta: 'Δ',
  lambda: 'λ',
  mu: 'μ',
  sigma: 'σ',
  omega: 'ω',
  Omega: 'Ω',
};
const MATH_FUNCTIONS = new Set(
  'sin cos tan log ln exp lim max min sec csc cot'.split(' ')
);
// Commands whose braced argument is just text: `\text{kg}` -> `kg`.
const TEXT_COMMANDS =
  /\\(?:text|textbf|mathrm|mathbf|mathit|mathbb|operatorname|ce|vec|overline|hat)\s*\{([^{}]*)\}/g;
const FRACTION = /\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g;

function commandText(name: string): string {
  if (name in MATH_SYMBOLS) return MATH_SYMBOLS[name];
  return MATH_FUNCTIONS.has(name) ? name : '';
}

/** One LaTeX expression as plain text: `\frac{a}{b}` -> `a/b`, `CO_2` -> `CO2`. */
function latexToText(tex: string): string {
  let text = tex;
  // Twice, so a fraction inside a fraction unwraps too.
  for (let pass = 0; pass < 2; pass += 1) {
    text = text.replace(TEXT_COMMANDS, '$1').replace(FRACTION, '$1/$2');
  }
  return text
    .replace(/\\sqrt\s*\{([^{}]*)\}/g, '√$1')
    .replace(/\\[,;:! ]/g, ' ')
    .replace(/\\([A-Za-z]+)/g, (_, name: string) => commandText(name))
    .replace(/[{}_]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Replace every math span (`$...$`, `$$...$$`, `\(...\)`, `\[...\]`) with a
 * plain-text reading of it - for previews and for `title` / `aria-label`
 * attributes, which cannot hold KaTeX.
 */
export function stripMath(text: string): string {
  const plain = (_: string, tex: string) => latexToText(tex);
  return text
    .replace(/\$\$([\s\S]+?)\$\$/g, plain)
    .replace(/\\\[([\s\S]+?)\\\]/g, plain)
    .replace(/\\\(([\s\S]+?)\\\)/g, plain)
    .replace(/\$([^$\n]+?)\$/g, plain);
}

/**
 * A one-paragraph, plain-text preview of a markdown answer - for a list row
 * that shows the start of an answer under its question (`QACard`,
 * `DeepDiveCard`). Rendering the markdown there would put headings, lists and
 * code blocks into a two-line clamp; this keeps the words and drops the
 * markup.
 *
 * Deliberately small: it removes the syntax the tutor's answers actually use
 * (emphasis, headings, lists, quotes, inline code, links, images, fences and
 * `[n]` citation markers), reads maths as plain text (`stripMath`) and
 * collapses whitespace. It is not a markdown parser, and the full answer is
 * always one tap away.
 */
export function previewText(markdown: string): string {
  // Fenced code: keep nothing - a code sample is not a preview. Maths goes
  // before emphasis, so `$CO_2$` never reads as half an underscore pair.
  return (
    stripMath(markdown.replace(/```[\s\S]*?```/g, ' '))
      // Images, then links: keep the link text.
      .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      // Citation markers like [1] or [1, 2], with the space before them.
      .replace(/\s*\[\d+(?:\s*,\s*\d+)*\]/g, '')
      // Line-leading headings, quotes and list markers.
      .replace(/^\s{0,3}(?:#{1,6}\s+|>\s?|[-*+]\s+|\d+[.)]\s+)/gm, '')
      // Emphasis and inline code.
      .replace(/(\*\*|__|\*|_|`)(.+?)\1/g, '$2')
      .replace(/\s+/g, ' ')
      .trim()
  );
}
